import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, CircleAlert } from '@/components/icons'
import { Button } from '../ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer'
import { PrntrCard } from '../ui/prntr-input'
import { SelectRowField, TextRow } from '../ui/prntr-fields'
import { usePassengerQuickAdd } from '../../lib/hooks/passenger'
import { useQueryClient } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../../lib/orpc'
import { PassengerRole } from '@@api/shared'

interface AddedPax {
  firstName: string
  lastName: string
  avatarColor: string
}

const quickPaxSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  role: z.enum([PassengerRole.Primary, PassengerRole.Guest]).default(PassengerRole.Primary),
})

type QuickPaxForm = z.infer<typeof quickPaxSchema>

interface QuickStartPaxDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function QuickStartPaxDrawer({ isOpen, onClose }: QuickStartPaxDrawerProps) {
  const [addedPax, setAddedPax] = useState<AddedPax[]>([])
  const paxQuickAdd = usePassengerQuickAdd()
  const queryClient = useQueryClient()
  const orpc = useOrpcTanstackQueryUtils()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<QuickPaxForm>({
    resolver: zodResolver(quickPaxSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: PassengerRole.Primary,
    },
    mode: 'onChange',
  })

  const handleSave = async (data: QuickPaxForm, addAnother: boolean) => {
    const created = await paxQuickAdd.mutateAsync({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email || undefined,
      role: data.role,
    })

    setAddedPax((prev) => [
      ...prev,
      { firstName: data.firstName, lastName: data.lastName, avatarColor: created.avatarColor },
    ])

    if (addAnother) {
      reset()
    } else {
      queryClient.invalidateQueries({ queryKey: orpc.onboarding.key() })
      handleClose()
    }
  }

  const handleClose = () => {
    reset()
    setAddedPax([])
    onClose()
  }

  const getInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
        <DrawerHeader className="flex items-center justify-between p-4 shrink-0">
          <DrawerTitle className="text-lg font-bold text-content-primary-bold tracking-[-1px]">
            Add Passengers
          </DrawerTitle>
          <button type="button" onClick={handleClose} className="p-1 hover:bg-surface-L2 rounded transition-colors">
            <X className="w-4 h-4 text-content-primary-bold" />
          </button>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Info banner */}
          <div className="bg-surface-L2 rounded-md flex gap-3 items-center px-3 py-2 mb-2">
            <CircleAlert className="w-5 h-5 text-content-primary-subtle shrink-0" />
            <p className="text-sm text-content-primary-subtle tracking-[0.07px] leading-normal">
              Add passengers to the PAX profiles directory. You can add more details and upload documents later.
            </p>
          </div>

          {/* Added pax avatars */}
          {addedPax.length > 0 && (
            <div className="flex items-center pl-2 pr-4 py-4">
              {addedPax.map((pax, i) => (
                <div
                  key={i}
                  className="size-8 rounded-full flex items-center justify-center text-sm font-medium text-white -mr-2"
                  style={{ backgroundColor: pax.avatarColor }}
                >
                  {getInitials(pax.firstName, pax.lastName)}
                </div>
              ))}
            </div>
          )}

          {/* Form */}
          <div className="flex flex-col gap-3 p-2">
            <PrntrCard title="Personal Details">
              <TextRow nullable={false}
                control={control}
                name="firstName"
                label="First Name *"
                placeholder="First name"
                error={errors.firstName?.message}
              />
              <TextRow nullable={false}
                control={control}
                name="lastName"
                label="Last Name *"
                placeholder="Last name"
                error={errors.lastName?.message}
              />
              <TextRow nullable={false}
                control={control}
                name="email"
                label="Email Address"
                type="email"
                placeholder="email@example.com"
                error={errors.email?.message}
              />
              <SelectRowField
                control={control}
                name="role"
                label="Role"
                options={[
                  { value: PassengerRole.Primary, label: 'Primary' },
                  { value: PassengerRole.Guest, label: 'Guest' },
                ]}
                placeholder="Select role"
              />
            </PrntrCard>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 shrink-0">
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="flex-1"
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit((data) => handleSave(data, true))}
            >
              Save & Add Another
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="flex-1"
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit((data) => handleSave(data, false))}
            >
              Save & Continue Setup
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
